import { tracker } from '@lessonup/analytics';
import { logger, useDocumentMutation } from '@lessonup/client-integration';
import {
  AsyncButton,
  Button,
  ErrorBanner,
  IconAdd,
  IconComponentImage,
  IconCopy,
  SelectField,
  SpaceBetween,
  spacing,
  styled,
  useErrorContext,
  useForm,
} from '@lessonup/ui-components';
import { asError, randomId } from '@lessonup/utils';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { FormProvider } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { FullPageBodySection } from '../../../components/FullPageFlow/FullPageBodySection';
import { AiToolboxGenerateImageInput } from '../../../types/graphql';
import { useEditorContext } from '../../Editor/context/EditorContext/EditorContext';
import { useActivePin } from '../../Editor/context/EditorContext/hooks/useEditorState';
import {
  Upload,
  useCreateImagePinComponentsWithUploads,
  useCreateImageUploads,
} from '../../Editor/hooks/useCreateImagePinComponents';
import { useEditorUploadsStatus } from '../../Editor/utils/yjs/EditorUploadsYjs/useEditorUploads';
import { aiToolsTypeToEventType, ImageGenerator } from '../AiToolbox.types';
import { i18nextNamespaceAiTools } from '../AiToolbox.utils';
import { useAiToolboxContext } from '../context/AiToolboxContext';
import { CappedTextArea } from '../helpers/CappedTextArea';
import { DefaultToolWrapper, FullWidth } from '../helpers/DefaultToolWrapper';
import { ThumbUpAndDownFeedback } from '../helpers/ThumbUpAndDownFeedback';
import { AiToolboxFormWrapper } from '../helpers/ToolForm';
import { tipTapJSONToHtml } from '../helpers/tiptap';
import { useFindOrCreateAiFolder } from '../hooks';
import { useInitialBodyFormUpdate } from '../hooks/useInitialBodyFormUpdate';

interface AiToolboxGenerateImageProps {
  generator: ImageGenerator;
}

export function AiToolboxGenerateImage({ generator }: AiToolboxGenerateImageProps) {
  const { initialBody, showInline } = useAiToolboxContext();
  const methods = useForm<AiToolboxGenerateImageInput>({
    defaultValues: {
      subject: tipTapJSONToHtml(initialBody),
      imageStyle: 'photo',
    },
  });

  const { register, handleSubmit, setValue } = methods;
  const { t } = useTranslation(i18nextNamespaceAiTools);
  const { setError, clearError } = useErrorContext();
  const [generateImageMutation] = useDocumentMutation(generator.document, {
    onError: (error) => {
      setError({ error, displayMode: 'banner' });
      logger.error(error);
    },
  });

  useInitialBodyFormUpdate(setValue, 'subject');

  const [isLoading, setIsLoading] = useState(false);
  const [imageDataUrl, setImageDataUrl] = useState<string | null>(null);
  const [imageBlob, setImageBlob] = useState<Blob | null>(null);
  const [imageName, setImageName] = useState<string | null>(null);
  const { uploadGeneratedImage, isUploading, setUploadIdentifier } = useGeneratedImageUpload(imageBlob, imageName);

  const onSubmit = async (input: AiToolboxGenerateImageInput) => {
    clearError();
    setIsLoading(true);

    try {
      const { data: mutationResponse, errors } = await generateImageMutation({
        variables: {
          input,
        },
      });

      if (!mutationResponse || errors?.length) {
        // these are handeled by onError above
      } else {
        const { imageUrl, imageName } = mutationResponse.aiToolboxGenerateImage;
        setImageName(imageName);

        // We need the image blob to add an image pin, set the background or copy the image to the clipboard
        const fetchResponse = await fetch(imageUrl);
        const blob = await fetchResponse.blob();
        setImageBlob(blob);

        // And we need the data url to display the image preview
        const reader = new FileReader();
        reader.onloadend = () => {
          setImageDataUrl(reader.result as string);
        };
        reader.readAsDataURL(blob);

        tracker.events.aiToolsImageCreate({
          promptLength: input.subject?.length ?? 0,
          aspectRatio: input.aspectRatio ?? '',
          imageStyle: input.imageStyle ?? '',
          detailedStyle: input.detailedStyle ?? '',
        });
      }
    } catch (error) {
      setError({ error: asError(error), displayMode: 'banner' });
      logger.error(asError(error));
    } finally {
      setIsLoading(false);
    }
  };

  const imageOutput = imageDataUrl ? (
    <>
      <StyledImage src={imageDataUrl} alt={t('generatedImageAlt')} />
      <StyledButtonWrapper>
        {showInline && (
          <>
            <SetBackgroundButton
              disabled={!imageBlob}
              uploadGeneratedImage={uploadGeneratedImage}
              isUploading={isUploading}
              setUploadIdentifier={setUploadIdentifier}
            />
            <AddPinButton
              disabled={!imageBlob}
              generator={generator}
              uploadGeneratedImage={uploadGeneratedImage}
              isUploading={isUploading}
              setUploadIdentifier={setUploadIdentifier}
            />
          </>
        )}
        {!showInline && <CopyImageButton imageDataUrl={imageDataUrl} generator={generator} />}
        <ThumbUpAndDownFeedback aiToolType={generator.type} />
      </StyledButtonWrapper>
    </>
  ) : null;

  return (
    <DefaultToolWrapper>
      <AiToolboxFormWrapper generator={generator}>
        <FormProvider {...methods}>
          <SpaceBetween direction="y" spacing={showInline ? spacing.size16 : spacing.size32}>
            <CappedTextArea
              field="subject"
              required
              size="small"
              label={t('generateImageSubject')}
              placeholder={t('generateImageSubjectPlaceholder')}
              instructions={t('generateImageSubjectInstructions')}
            />
            <SelectField
              size="medium"
              label={t('imageAspectRatio')}
              options={[
                { label: t('dropdownOptionPlaceholder'), value: '' },
                { label: '1:1', value: '1:1' },
                { label: '16:9', value: '16:9' },
                { label: '9:16', value: '9:16' },
                { label: '4:3', value: '4:3' },
                { label: '3:4', value: '3:4' },
              ]}
              {...register('aspectRatio', {
                setValueAs: (value: string) => (value.trim() === '' ? undefined : value),
              })}
            />

            <SelectField
              size="medium"
              label={t('generatorImageStyle')}
              options={[
                { label: t('dropdownOptionPlaceholder'), value: '' },
                { label: t('imageStylePhotorealistic'), value: 'photo' },
                { label: t('imageStyleIllustration'), value: 'illustration' },
                { label: t('imageStyleCinematic'), value: 'cinematic photo' },
                { label: t('imageStyleSketch'), value: 'sketch' },
                { label: t('imageStyleCartoon'), value: 'cartoon' },
                { label: t('imageStyleModern'), value: 'modern art' },
              ]}
              {...register('imageStyle', {
                setValueAs: (value: string) => (value.trim() === '' ? undefined : value),
              })}
            />
            <SelectField
              size="medium"
              label={t('generatorDetailedStyle')}
              options={[
                { label: t('dropdownOptionPlaceholder'), value: '' },
                { label: t('imageDetailedStyleSciFi'), value: 'sci fi' },
                { label: t('imageDetailedStyleMoody'), value: 'moody' },
                { label: t('imageDetailedStyleFoggy'), value: 'foggy' },
                { label: t('imageDetailedStyleCloseUp'), value: 'close up' },
                { label: t('imageDetailedStyleIntricate'), value: 'intricate' },
                { label: t('imageDetailedStyleArchitectural'), value: 'architectural' },
                { label: t('imageDetailedStyleSymmetrical'), value: 'symmetrical' },
                { label: t('imageDetailedStyleCinematicLighting'), value: 'cinematic lighting' },
                { label: t('imageDetailedStyleBlur'), value: 'blur' },
                { label: t('imageDetailedStyleSunset'), value: 'sunset' },
                { label: t('imageDetailedStyleColourful'), value: 'colourful' },
              ]}
              {...register('detailedStyle', {
                setValueAs: (value: string) => (value.trim() === '' ? undefined : value),
              })}
            />

            <ErrorBanner />

            <Button loading={isLoading} onClick={handleSubmit(onSubmit)}>
              {t('generateImageCta')}
            </Button>
          </SpaceBetween>
        </FormProvider>
      </AiToolboxFormWrapper>

      {showInline && imageDataUrl ? (
        <Wrapper>{imageOutput}</Wrapper>
      ) : (
        <FullPageBodySection>
          <FullWidth>{imageOutput}</FullWidth>
        </FullPageBodySection>
      )}
    </DefaultToolWrapper>
  );
}

type UploadIdentifier =
  | { type: 'pinComponent'; pinId: string; pinComponentId: string }
  | { type: 'background'; pinId: string };

// Shared state for the generated image upload
const useGeneratedImageUpload = (imageBlob: Blob | null, imageName: string | null) => {
  const [upload, setUpload] = useState<Upload | null>(null);
  const [isUploadInProgress, setIsUploadInProgress] = useState(false);
  const [uploadIdentifier, setUploadIdentifier] = useState<UploadIdentifier | null>(null);
  const { setError } = useErrorContext();
  const createImageUploads = useCreateImageUploads();
  const isUploadPending = useEditorUploadsStatus();
  const { findOrCreateAiFolder } = useFindOrCreateAiFolder();

  // This is a random ID to track the upload state.
  // If the user generates a new image while an upload is in progress, we want to cancel the upload.
  const uploadToken = useRef(randomId());

  // Reset upload state when dependencies change
  useEffect(() => {
    setUpload(null);
    setIsUploadInProgress(false);
    setUploadIdentifier(null);
    uploadToken.current = randomId();
  }, [imageBlob, imageName]);

  const uploadGeneratedImage = useCallback(async (): Promise<Upload | null> => {
    const currentUploadToken = uploadToken.current;

    // If we already have an upload ID, return it
    if (upload) return upload;

    if (!imageBlob || !imageName) {
      return upload;
    }

    // Create a File object from the cached blob
    const file = new File([imageBlob], `${imageName}.png`, { type: 'image/png' });

    setIsUploadInProgress(true);

    // Get the AI folder ID
    const aiFolderId = await findOrCreateAiFolder();

    try {
      const [{ uploadId }] = await createImageUploads([file], { folderId: aiFolderId || undefined });
      if (!uploadId) {
        throw new Error('Upload failed');
      }

      // A new image was generated while the upload was in progress. Cancel this upload.
      if (currentUploadToken !== uploadToken.current) {
        return null;
      }

      setUpload({ uploadId, file });
      return { uploadId, file };
    } catch (error) {
      setError({ error: asError(error), displayMode: 'banner' });
      return null;
    } finally {
      setIsUploadInProgress(false);
    }
  }, [upload, createImageUploads, setError, imageBlob, imageName, findOrCreateAiFolder]);

  // Check if any tracked upload is pending
  const isPinUploadPending =
    uploadIdentifier?.type === 'pinComponent' &&
    isUploadPending({
      type: 'pinComponent',
      pinId: uploadIdentifier.pinId,
      pinComponentId: uploadIdentifier.pinComponentId,
    });
  const isBackgroundUploadPending =
    uploadIdentifier?.type === 'background' && isUploadPending({ type: 'pin', pinId: uploadIdentifier.pinId });
  const isUploading = isUploadInProgress || isPinUploadPending || isBackgroundUploadPending;

  return {
    uploadGeneratedImage,
    isUploading,
    setUploadIdentifier,
  };
};

interface SetBackgroundButtonProps {
  disabled: boolean;
  uploadGeneratedImage: () => Promise<Upload | null>;
  isUploading: boolean;
  setUploadIdentifier: (uploadIdentifier: UploadIdentifier) => void;
}

function SetBackgroundButton({
  disabled,
  uploadGeneratedImage,
  isUploading,
  setUploadIdentifier,
}: SetBackgroundButtonProps) {
  const { t } = useTranslation(i18nextNamespaceAiTools);
  const pin = useActivePin();
  const { setError } = useErrorContext();
  const { uploadDispatch } = useEditorContext();

  const onSetBackground = async () => {
    if (!pin) {
      return;
    }

    try {
      // Upload the file first to get the upload ID
      const upload = await uploadGeneratedImage();
      if (!upload) {
        throw new Error('Upload failed');
      }

      // Track this pin ID for upload state
      setUploadIdentifier({ type: 'background', pinId: pin.id });

      // Create the upload entry for background image
      uploadDispatch({
        action: 'CreateUpload',
        upload: {
          type: 'PinBackgroundImage',
          pinId: pin.id,
          uploadId: upload.uploadId,
        },
        skipUndoManager: false,
      });

      tracker.events.backgroundAdd({
        mediaCreationMethod: 'generate_with_ai',
      });
    } catch (error) {
      setError({ error: asError(error), displayMode: 'banner' });
      logger.error(asError(error));
    }
  };

  return (
    <Button
      disabled={disabled}
      onClick={onSetBackground}
      loading={isUploading}
      buttonType="neutral"
      showStroke
      iconStart={<IconComponentImage />}
    >
      {t('imageGeneratorSetBackground')}
    </Button>
  );
}

interface AddPinButtonProps {
  disabled: boolean;
  generator: ImageGenerator;
  uploadGeneratedImage: () => Promise<Upload | null>;
  isUploading: boolean;
  setUploadIdentifier: (uploadIdentifier: UploadIdentifier) => void;
}

function AddPinButton({
  disabled,
  generator,
  uploadGeneratedImage,
  isUploading,
  setUploadIdentifier,
}: AddPinButtonProps) {
  const { t } = useTranslation(i18nextNamespaceAiTools);
  const pin = useActivePin();
  const { setError } = useErrorContext();
  const createImagePinComponentsWithUploads = useCreateImagePinComponentsWithUploads();

  const onAddPin = async () => {
    if (!pin) {
      return;
    }

    try {
      // Upload the file first to get the upload ID
      const upload = await uploadGeneratedImage();
      if (!upload) {
        throw new Error('Upload failed');
      }

      // Create pin components with the existing upload ID
      const pinComponentIds = await createImagePinComponentsWithUploads(
        pin,
        [upload],
        aiToolsTypeToEventType[generator.type]
      );

      // Track the first pin component ID for upload state
      if (pinComponentIds.length > 0) {
        setUploadIdentifier({ type: 'pinComponent', pinId: pin.id, pinComponentId: pinComponentIds[0] });
      }
    } catch (error) {
      setError({ error: asError(error), displayMode: 'banner' });
      logger.error(asError(error));
    }
  };

  return (
    <Button
      disabled={disabled}
      onClick={onAddPin}
      loading={isUploading}
      buttonType="neutral"
      showStroke
      iconStart={<IconAdd />}
    >
      {t('imageGeneratorAddToPin')}
    </Button>
  );
}

interface CopyImageButtonProps {
  imageDataUrl: string | null;
  generator: ImageGenerator;
}

function CopyImageButton({ imageDataUrl, generator }: CopyImageButtonProps) {
  const { t } = useTranslation(i18nextNamespaceAiTools);
  const [isCopied, setIsCopied] = useState(false);

  const handleCopy = async () => {
    if (!imageDataUrl) return;
    await copyBase64ImageToClipboard(imageDataUrl);
    setIsCopied(true);
    tracker.events.aiToolsCopy({
      aiToolType: aiToolsTypeToEventType[generator.type],
    });
    setTimeout(() => setIsCopied(false), 2000);
  };

  return (
    <AsyncButton onClick={handleCopy} buttonType="neutral" iconStart={<IconCopy />} showStroke>
      {isCopied ? t('copiedOutput') : t('copyOutput')}
    </AsyncButton>
  );
}

async function copyBase64ImageToClipboard(base64DataURL: string) {
  // 1. Split off the metadata from the Base64 string
  const [meta, base64] = base64DataURL.split(',');
  //    meta looks like "data:image/png;base64"
  const mime = meta?.match(/data:(.*);base64/)?.[1] ?? 'image/png';

  // 2. Decode the Base64 data to a binary string
  const binary = atob(base64);

  // 3. Create an ArrayBuffer and a Uint8Array to hold the bytes
  const len = binary.length;
  const buffer = new ArrayBuffer(len);
  const view = new Uint8Array(buffer);
  for (let i = 0; i < len; i++) {
    view[i] = binary.charCodeAt(i);
  }

  // 4. Make a Blob from the buffer
  const blob = new Blob([buffer], { type: mime });

  // 5. Create a ClipboardItem from the Blob
  const clipboardItem = new ClipboardItem({ [mime]: blob });

  // 6. Write it to the clipboard
  await navigator.clipboard.write([clipboardItem]);
}

const StyledImage = styled.img`
  max-width: 100%;
  max-height: 500px;
  height: auto;
  object-fit: contain;
  border-radius: 8px;
  margin-bottom: 16px;
`;

const Wrapper = styled.div`
  padding-top: ${spacing.size16};
`;

const StyledButtonWrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${spacing.size8};

  @media print {
    display: none;
  }
`;
